import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { navigation } from '@good-season/platform';
import { ensurePrimitiveStyles, ErrorBoundary } from '@good-season/ui';

import '@fontsource-variable/archivo';
import '@fontsource-variable/public-sans';
import '@fontsource-variable/jetbrains-mono';
import './styles/tokens.css';
import './styles/screens.css';
import './styles/scorebook.css';
import './styles/boxscore.css';
/*
 * ORDER MATTERS between these two, and the failure is silent.
 *
 * `shell.css` is the mobile-first base and `shell-wide.css` is nothing but a
 * `@media (min-width: 640px)` block. A media query adds no specificity, so if
 * the wide sheet were imported first every one of its rules would lose to the
 * base and a laptop would quietly render the phone layout. Nothing would error.
 */
import './styles/shell.css';
import './styles/shell-wide.css';
import './styles/home.css';
import './styles/flyer.css';
import './styles/announcements.css';

import { App } from './App.js';
import { installDiagnostics } from './help/diagnostics.js';
import { registerServiceWorker } from './pwa/registerServiceWorker.js';
import { setWaitingUpdate } from './pwa/update-store.js';

const container = document.getElementById('root');
if (container === null) {
  throw new Error('The root element is missing from index.html.');
}

// The primitive stylesheet is a string inside the package, not a CSS file Vite
// can see, so nothing injects it unless something asks. Called here rather than
// from a component so that the first paint is already styled: a Dialog that
// mounts unstyled is a full-bleed white rectangle for one frame.
ensurePrimitiveStyles();

// Registered before the first paint rather than after, so the shell is being
// cached while a parent reads the screen. BUILD_PLAN 5.8 and rubric F1: the
// scorebook has to open from a cold start with zero network, and it can only
// do that if a previous visit cached it.
registerServiceWorker({
  /*
   * The handler that did not exist, which is why no deploy could reach anybody.
   *
   * `registerServiceWorker` calls `handlers.onUpdateAvailable?.(apply)`, so
   * without this the `apply` it hands out was reachable from nothing: every new
   * version installed, entered `waiting`, and stayed there forever. A returning
   * browser was frozen on whatever it first cached, invisibly, because a fresh
   * browser and a curl both see the new bundle.
   *
   * It stores rather than applies. `UpdatePrompt` offers it and the person
   * decides, which is the policy `registerServiceWorker` documents at length.
   */
  onUpdateAvailable: (apply) => {
    setWaitingUpdate(apply);
  },
  onOfflineReady: () => {
    document.documentElement.dataset['gsOfflineReady'] = 'true';
  },
});

installDiagnostics();

createRoot(container).render(
  <StrictMode>
    {/*
      Outermost, so a render error anywhere shows the themed fallback with a
      diagnostic code instead of a blank page. The scorebook has its own
      boundary inside `App`.
    */}
    <ErrorBoundary
      fills="screen"
      secondaryAction={{
        label: 'Go to the start',
        onSelect: () => {
          // A full load of the manifest `start_url` (the Vite base), because
          // a crash outside the scorebook may repeat on every render of the
          // current URL and the root sits outside the router.
          navigation.goToStart(import.meta.env.BASE_URL);
        },
      }}
    >
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
